/**
 * CR458: Reusable Image Capture mapping editor.
 * Extracted from SourceMappingTab's MappingPanel for use in EditSourceModal.
 * CR459: Optional adapter UX (no adapter = default PDF pipeline, not an error).
 *        Test Capture button + results panel. Quick links to Adapter Registry.
 */

import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MapPin,
  ExternalLink,
  FlaskConical,
  Loader2,
  CheckCircle,
  XCircle,
  Layers,
  SkipForward,
  Sparkles,
  AlertTriangle,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { validateMapping, generateFixSuggestions } from "@/lib/imageCapture/validateMapping";
import type { FlyerSourceRow } from "@/lib/imageCapture/validateMapping";
import type { ImageCaptureAdapter } from "@/components/admin/image-capture-studio/useAdapters";
import { useAdapterRecommendations } from "./useAdapterRecommendations";

// ─── Types ───────────────────────────────────────────────────────────────────

type HealthLevel = "OK" | "WARN" | "ERROR";

export interface MappingPayload {
  flyer_image_capture_enabled: boolean;
  flyer_image_capture_adapter_key: string | null;
  flyer_image_capture_params: unknown | null;
}

export interface MappingEditorProps {
  source: FlyerSourceRow;
  adapters: ImageCaptureAdapter[];
  /** Called when user clicks Save. Return true on success. */
  onSave: (sourceId: string, payload: MappingPayload) => Promise<boolean>;
  /** Optional: show a link to open source in Studio */
  studioHref?: string;
  /** Layout mode — "modalSection" renders inline without Sheet wrapper */
  mode?: "modalSection" | "sidePanel";
  /** If true, disable all inputs */
  disabled?: boolean;
}

type TestResultSkipped = { ok: true; skipped: true; reason: string; message?: string };
type TestResultQueued = { ok: true; skipped: false; test_run_id: string; status: string; adapter_key?: string; adapter_display_name?: string; ui_url?: string; message?: string };
type TestResultError = { ok: false; error: string; reason?: string };
type TestResult = TestResultSkipped | TestResultQueued | TestResultError;

// ─── Sentinels ────────────────────────────────────────────────────────────────

const NONE = "__none__";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function safeJson(val: unknown): string {
  if (val == null) return "";
  if (typeof val === "string") return val;
  return JSON.stringify(val, null, 2);
}

function tryParse(str: string): unknown | null {
  if (!str.trim()) return null;
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function HealthBadge({ health }: { health: HealthLevel }) {
  const variants: Record<HealthLevel, string> = {
    OK: "bg-primary text-primary-foreground",
    WARN: "bg-secondary text-secondary-foreground border border-border",
    ERROR: "bg-destructive text-destructive-foreground",
  };
  const labels: Record<HealthLevel, string> = {
    OK: "✓ OK",
    WARN: "⚠ WARN",
    ERROR: "✗ ERROR",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${variants[health]}`}
    >
      {labels[health]}
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function MappingEditor({
  source,
  adapters,
  onSave,
  studioHref,
  disabled = false,
}: MappingEditorProps) {
  const [captureEnabled, setCaptureEnabled] = useState(
    source.flyer_image_capture_enabled ?? false
  );
  const [adapterKey, setAdapterKey] = useState(
    source.flyer_image_capture_adapter_key ?? ""
  );
  const [paramsJson, setParamsJson] = useState(
    safeJson(source.flyer_image_capture_params)
  );
  const [saving, setSaving] = useState(false);
  const [jsonError, setJsonError] = useState("");
  const [saved, setSaved] = useState(false);

  // CR459: Test Capture state
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [logsExpanded, setLogsExpanded] = useState(false);

  // CR460: recommendations
  const { recommendations, loading: recsLoading } = useAdapterRecommendations(
    source.merchant_source_id || null
  );
  // CR460-P: "Keep current params" checkbox for Apply Suggestion
  const [keepCurrentParams, setKeepCurrentParams] = useState(false);

  // Reset state whenever the source changes (stable reference by ID)
  useEffect(() => {
    setCaptureEnabled(source.flyer_image_capture_enabled ?? false);
    setAdapterKey(source.flyer_image_capture_adapter_key ?? "");
    setParamsJson(safeJson(source.flyer_image_capture_params));
    setJsonError("");
    setSaved(false);
    setTestResult(null);
    setKeepCurrentParams(false);
  }, [source.merchant_source_id]);

  const handleAdapterChange = (key: string) => {
    const resolved = key === NONE ? "" : key;
    setAdapterKey(resolved);
    setSaved(false);
    setTestResult(null);
    if (resolved && !paramsJson.trim()) {
      const adapter = adapters.find((a) => a.adapter_key === resolved);
      if (adapter?.default_params) setParamsJson(safeJson(adapter.default_params));
    }
  };

  // CR460-P: apply suggestion — respects "Keep current params" toggle
  const handleApplySuggestion = async (suggestedKey: string) => {
    const adapter = adapters.find((a) => a.adapter_key === suggestedKey);
    setAdapterKey(suggestedKey);
    setCaptureEnabled(true);
    setSaved(false);
    setTestResult(null);

    let paramsToSave: unknown | null;
    if (keepCurrentParams) {
      // User explicitly wants to persist the current editor state
      paramsToSave = paramsJson ? tryParse(paramsJson) : null;
    } else {
      // Safe default: don't touch existing DB params.
      // If source has no params + adapter has defaults → prefill defaults.
      const sourceHasParams = !!source.flyer_image_capture_params &&
        (typeof source.flyer_image_capture_params !== "object" ||
          Object.keys(source.flyer_image_capture_params as object).length > 0);
      if (!sourceHasParams && adapter?.default_params) {
        paramsToSave = adapter.default_params;
        setParamsJson(safeJson(adapter.default_params));
      } else {
        // Preserve whatever is already in DB (don't send anything that overwrites)
        paramsToSave = source.flyer_image_capture_params ?? null;
        if (!paramsJson.trim()) setParamsJson(safeJson(source.flyer_image_capture_params));
      }
    }

    setSaving(true);
    const ok = await onSave(source.merchant_source_id, {
      flyer_image_capture_enabled: true,
      flyer_image_capture_adapter_key: suggestedKey,
      flyer_image_capture_params: paramsToSave,
    });
    setSaving(false);
    if (ok) setSaved(true);
  };

  const handleSave = async () => {
    if (paramsJson && !tryParse(paramsJson)) {
      setJsonError("Invalid JSON in adapter params");
      return;
    }
    setJsonError("");
    setSaving(true);
    const ok = await onSave(source.merchant_source_id, {
      flyer_image_capture_enabled: captureEnabled,
      flyer_image_capture_adapter_key: adapterKey || null,
      flyer_image_capture_params: paramsJson ? tryParse(paramsJson) : null,
    });
    setSaving(false);
    if (ok) setSaved(true);
  };

  // CR459: Test Capture — call edge function
  const handleTestCapture = async () => {
    setTestRunning(true);
    setTestResult(null);
    setLogsExpanded(false);
    try {
      const { data, error } = await supabase.functions.invoke("image-capture-test", {
        body: { merchant_source_id: source.merchant_source_id, max_pages: 3 },
      });
      if (error) {
        setTestResult({ ok: false, error: error.message || "Test request failed" });
      } else {
        setTestResult(data as TestResult);
      }
    } catch (e) {
      setTestResult({ ok: false, error: e instanceof Error ? e.message : "Unexpected error" });
    } finally {
      setTestRunning(false);
    }
  };

  const hasAdapter = !!adapterKey;
  const liveSource: FlyerSourceRow = {
    ...source,
    flyer_image_capture_enabled: captureEnabled,
    flyer_image_capture_adapter_key: adapterKey || null,
    adapter_enabled: adapters.find((a) => a.adapter_key === adapterKey)?.enabled ?? null,
  };
  const validationResult = hasAdapter ? validateMapping(liveSource) : null;
  const fixSuggestions = hasAdapter ? generateFixSuggestions(liveSource, validationResult!) : [];
  const selectedAdapter = adapters.find((a) => a.adapter_key === adapterKey);
  const registryHref = "/admin/image-capture?tab=adapters";
  const viewAdapterHref = adapterKey
    ? `/admin/image-capture?tab=adapters&adapter_key=${encodeURIComponent(adapterKey)}`
    : null;

  return (
    <div className="space-y-5">
      {/* Region banner */}
      {source.requires_region && (
        <div className="flex items-center gap-2 text-sm bg-muted rounded-md px-3 py-2">
          <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
          <span>Region required — postal code: <strong>{source.default_postal_code ?? "not set"}</strong></span>
        </div>
      )}

      {/* CR460: Suggested adapters — skeleton while loading, empty state when none */}
      {recsLoading ? (
        <div className="rounded-md border bg-muted/30 space-y-2 p-3 animate-pulse">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">✨ Suggestions (based on URL + rules)</span>
          </div>
          <div className="space-y-1.5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-8 rounded-md bg-muted/60" />
            ))}
          </div>
        </div>
      ) : recommendations.length === 0 ? (
        <div className="rounded-md border border-dashed px-3 py-2 text-center">
          <p className="text-[11px] text-muted-foreground">
            No suggestions — add match rules in Studio → Adapters to enable auto-matching.
          </p>
        </div>
      ) : (
        <div className="rounded-md border bg-muted/30 space-y-2 p-3">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-medium">✨ Suggestions (based on URL + rules)</span>
            {adapterKey && (
              <span className="text-[10px] text-muted-foreground ml-1">
                Current: <code className="font-mono">{adapterKey}</code>
              </span>
            )}
          </div>
          {/* CR460-P: "Keep current params" checkbox */}
          <label className="flex items-center gap-1.5 cursor-pointer w-fit">
            <input
              type="checkbox"
              checked={keepCurrentParams}
              onChange={(e) => setKeepCurrentParams(e.target.checked)}
              className="h-3 w-3 accent-primary"
            />
            <span className="text-[10px] text-muted-foreground">Keep current params when applying</span>
          </label>
          <div className="space-y-1.5">
            {/* CR460-P: sort enabled adapters first */}
            {[...recommendations]
              .sort((a, b) => {
                if (a.adapter_enabled === b.adapter_enabled) return 0;
                return a.adapter_enabled ? -1 : 1;
              })
              .map((rec) => {
              const isCurrent = rec.recommended_adapter_key === adapterKey;
              return (
                <div
                  key={rec.recommended_adapter_key}
                  className={`flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 border bg-background ${isCurrent ? "border-primary/40" : ""}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-medium">{rec.adapter_display_name}</span>
                      <Badge variant="outline" className="text-[9px] px-1 font-mono">score {rec.score}</Badge>
                      {!rec.adapter_enabled && (
                        <Badge variant="secondary" className="text-[9px] px-1 flex items-center gap-0.5">
                          <AlertTriangle className="h-2.5 w-2.5" />disabled
                        </Badge>
                      )}
                      {isCurrent && <Badge variant="default" className="text-[9px] px-1">current</Badge>}
                    </div>
                    {(rec.reasons ?? []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {(rec.reasons ?? []).map((r) => (
                          <span key={r} className="text-[9px] text-muted-foreground bg-muted rounded px-1">{r}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant={isCurrent ? "secondary" : "outline"}
                    className="h-6 text-[10px] px-2 shrink-0"
                    disabled={saving || disabled || isCurrent || !rec.adapter_enabled}
                    title={!rec.adapter_enabled ? "Adapter is disabled — cannot apply" : isCurrent ? "Already applied" : "Apply this adapter"}
                    onClick={() => handleApplySuggestion(rec.recommended_adapter_key)}
                  >
                    {isCurrent ? "Applied" : "Apply"}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Enable toggle */}
      <div className="flex items-center gap-3">
        <Switch
          checked={captureEnabled}
          onCheckedChange={(v) => { setCaptureEnabled(v); setSaved(false); }}
          disabled={disabled}
        />
        <Label>Enable Image Capture</Label>
      </div>

      {/* Adapter select */}
      <div className="space-y-1">
        <Label>Adapter</Label>
        <Select
          value={adapterKey || NONE}
          onValueChange={handleAdapterChange}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select adapter…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>— None —</SelectItem>
            {adapters.map((a) => (
              <SelectItem
                key={a.adapter_key}
                value={a.adapter_key}
                disabled={!a.enabled}
              >
                {a.display_name}
                {!a.enabled && " (disabled)"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedAdapter && (
          <p className="text-xs text-muted-foreground font-mono truncate">
            {selectedAdapter.entrypoint}
          </p>
        )}

        {/* CR459: No-adapter helper — this is a valid state, not an error */}
        {!hasAdapter && (
          <div className="flex items-start gap-2 text-xs rounded-md border border-border bg-muted/40 px-3 py-2 mt-2">
            <SkipForward className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground">
              No adapter configured — this source uses the{" "}
              <strong>default FLYER PDF pipeline</strong>.{" "}
              Select an adapter above only if you want custom image capture.
            </span>
          </div>
        )}
      </div>

      {/* Param schema (read-only hint) — shown only when adapter selected */}
      {selectedAdapter?.param_schema && (
        <div className="space-y-1">
          <Label className="text-muted-foreground text-xs">Param Schema (read-only)</Label>
          <pre className="text-xs font-mono bg-muted rounded-md px-3 py-2 overflow-x-auto max-h-28 whitespace-pre-wrap">
            {safeJson(selectedAdapter.param_schema)}
          </pre>
        </div>
      )}

      {/* Params (editable) — shown only when adapter selected */}
      {hasAdapter && (
        <div className="space-y-1">
          <Label>Adapter Params (JSON)</Label>
          <Textarea
            value={paramsJson}
            onChange={(e) => {
              setParamsJson(e.target.value);
              setJsonError("");
              setSaved(false);
            }}
            className="font-mono text-xs min-h-[100px]"
            placeholder="{}"
            disabled={disabled}
          />
          {jsonError && (
            <p className="text-xs text-destructive">{jsonError}</p>
          )}
        </div>
      )}

      {/* Validation summary — only shown when adapter is configured */}
      {hasAdapter && validationResult && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label>Config Health</Label>
            <HealthBadge health={validationResult.health} />
          </div>
          {validationResult.issues.length > 0 && (
            <ul className="space-y-1">
              {validationResult.issues.map((iss, i) => (
                <li
                  key={`issue-${i}`}
                  className={`text-xs flex gap-2 ${
                    iss.level === "ERROR"
                      ? "text-destructive"
                      : "text-muted-foreground"
                  }`}
                >
                  <span>{iss.level === "ERROR" ? "✗" : "⚠"}</span>
                  <span>{iss.message}</span>
                </li>
              ))}
            </ul>
          )}
          {fixSuggestions.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                Fix suggestions ({fixSuggestions.length})
              </summary>
              <ul className="mt-1 space-y-1 pl-2">
                {fixSuggestions.map((s, i) => (
                  <li key={`fix-${i}`} className="font-mono text-muted-foreground bg-muted rounded px-2 py-1">
                    {s}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Actions row */}
      <div className="flex items-center gap-2 pt-1 flex-wrap">
        <Button
          onClick={handleSave}
          disabled={saving || disabled}
          size="sm"
        >
          {saving ? "Saving…" : saved ? "Saved ✓" : "Save Mapping"}
        </Button>

        {/* CR459: Test Capture button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleTestCapture}
          disabled={testRunning || disabled}
          className="gap-1.5"
        >
          {testRunning ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <FlaskConical className="h-3.5 w-3.5" />
          )}
          {testRunning ? "Testing…" : "Test Capture"}
        </Button>

        {studioHref && (
          <Button variant="ghost" size="sm" asChild>
            <a href={studioHref} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3.5 w-3.5 mr-1" />
              Open in Studio
            </a>
          </Button>
        )}
      </div>

      {/* CR459: Quick links */}
      <div className="flex items-center gap-3 text-xs">
        <a
          href={registryHref}
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
        >
          <Layers className="h-3 w-3" />
          Adapter Registry
        </a>
        {viewAdapterHref && (
          <a
            href={viewAdapterHref}
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ExternalLink className="h-3 w-3" />
            View adapter
          </a>
        )}
      </div>

      {/* CR459: Test Capture results panel */}
      {testResult && (
        <TestResultPanel
          result={testResult}
          logsExpanded={logsExpanded}
          onToggleLogs={() => setLogsExpanded((v) => !v)}
        />
      )}
    </div>
  );
}

// ─── TestResultPanel sub-component (receives explicit narrowed types) ────────

interface TestResultPanelProps {
  result: TestResult;
  logsExpanded: boolean;
  onToggleLogs: () => void;
}

function TestResultPanel({ result, logsExpanded, onToggleLogs }: TestResultPanelProps) {
  return (
    <div className="rounded-md border bg-muted/30 p-3 space-y-2 text-sm">
      {/* Status row */}
      <div className="flex items-center gap-2">
        {result.ok && !result.skipped && (
          <CheckCircle className="h-4 w-4 text-primary shrink-0" />
        )}
        {result.ok && result.skipped && (
          <SkipForward className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
        {!result.ok && (
          <XCircle className="h-4 w-4 text-destructive shrink-0" />
        )}
        <span className="font-medium">
          {result.ok && result.skipped
            ? "Test skipped — default pipeline"
            : result.ok
            ? "Test queued"
            : "Test failed"}
        </span>
        {result.ok && !result.skipped && (
          <Badge variant="outline" className="text-[10px]">QUEUED</Badge>
        )}
      </div>

      {/* Skipped */}
      {result.ok && result.skipped && (
        <p className="text-xs text-muted-foreground">
          {result.message ??
            "No adapter configured — this source uses the default FLYER PDF pipeline. Configure an adapter to run test capture."}
        </p>
      )}

      {/* Queued — cast to narrowed type at call-site; JSX conditions don't narrow union props */}
      {result.ok && !result.skipped && (
        <QueuedResultDetails result={result as TestResultQueued} logsExpanded={logsExpanded} onToggleLogs={onToggleLogs} />
      )}

      {/* Error */}
      {!result.ok && (
        <ErrorResultDetails result={result as TestResultError} />
      )}
    </div>
  );
}

function QueuedResultDetails({
  result,
  logsExpanded,
  onToggleLogs,
}: {
  result: TestResultQueued;
  logsExpanded: boolean;
  onToggleLogs: () => void;
}) {
  return (
    <div className="space-y-1">
      {result.message && (
        <p className="text-xs text-muted-foreground">{result.message}</p>
      )}
      <div className="flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">Adapter:</span>
        <code className="font-mono">{result.adapter_display_name ?? result.adapter_key}</code>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">Run ID:</span>
        <code className="font-mono truncate">{result.test_run_id}</code>
      </div>
      {result.ui_url && (
        <a
          href={result.ui_url}
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
        >
          <ExternalLink className="h-3 w-3" />
          View test run
        </a>
      )}
      <button
        type="button"
        onClick={onToggleLogs}
        className="block text-xs text-muted-foreground hover:text-foreground transition-colors mt-1"
      >
        {logsExpanded ? "▲ Hide details" : "▼ Show details"}
      </button>
      {logsExpanded && (
        <pre className="text-xs font-mono bg-muted rounded px-2 py-1 overflow-x-auto text-muted-foreground whitespace-pre-wrap">
          {`status: ${result.status}\ntest_run_id: ${result.test_run_id}\nadapter_key: ${result.adapter_key ?? ""}`}
        </pre>
      )}
    </div>
  );
}

function ErrorResultDetails({ result }: { result: TestResultError }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-destructive">{result.error}</p>
      {result.reason && (
        <p className="text-xs text-muted-foreground font-mono">{result.reason}</p>
      )}
    </div>
  );
}

